import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import api from '../utils/api';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      setSent(true);
      toast.success(data.message || 'Reset link sent if the account exists.');
    } catch (err) {
      // Endpoint returns generic success; only network errors land here
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] px-4">
      <div className="w-full max-w-md bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl shadow-lg p-8" data-testid="forgot-password-page">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] mb-6" data-testid="back-to-login-link">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>

        {sent ? (
          <div className="text-center py-4" data-testid="forgot-success">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Check your email</h1>
            <p className="text-[hsl(var(--muted-foreground))] text-sm">
              If an account exists for <span className="font-medium">{email}</span>, we've sent a link to reset your password. The link expires in 1 hour.
            </p>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 rounded-xl bg-[hsl(var(--primary))]/10 flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-[hsl(var(--primary))]" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Forgot your password?</h1>
            <p className="text-[hsl(var(--muted-foreground))] text-sm mb-6">
              Enter the email linked to your account and we'll send you a reset link.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
                  placeholder="you@example.com"
                  data-testid="forgot-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[hsl(var(--primary))] text-white py-2.5 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                data-testid="forgot-submit-button"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : 'Send reset link'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
